import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { LucideAngularModule } from 'lucide-angular';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';
import { QuizComponent } from './components/quiz/quiz.component';
import {
  Webinar,
  WebinarCardComponent,
} from './components/webinar-card/webinar-card.component';
import {
  ONBOARDING_VIDEO_URLS,
  RESOURCE_URLS,
  WEBINAR_REGISTRATION_URLS,
} from 'src/app/constants/document-urls';
import { HelpResourceBlock } from 'src/app/constants/journey-actions';

interface YouTubeVideo {
  titleKey: string;
  videoIdFr: string;
  altUrl: string;
}

interface ResourceFormat {
  type: string;
  urls: Record<string, string>;
}

interface Resource {
  /**
   * Prefix of the card's i18n keys: `.title`, `.description`, then one key per detail
   * row (`.objective`, `.when`, `.how`).
   */
  i18nKey: string;
  formats: ResourceFormat[];
  icon: string;
}

/** The labelled rows of a resource card, in display order. */
const RESOURCE_DETAILS = ['objective', 'when', 'how'] as const;

/**
 * The learning material of the help section: intro video, MOOCs, quiz, downloadable
 * documents and webinars.
 *
 * It used to sit below the journey on Getting Started, which made that page scroll through
 * five long content blocks before it was done. Getting Started is now the journey alone, and
 * every step pointing at one of these blocks links here with `?block=<id>`, which opens the
 * block and scrolls to it — see `JOURNEY_STEP_ACTIONS`.
 */
@Component({
  selector: 'app-help-resources',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    ButtonModule,
    LucideAngularModule,
    QuizComponent,
    WebinarCardComponent,
  ],
  templateUrl: './help-resources.component.html',
})
export class HelpResourcesComponent implements OnInit {
  private sanitizer = inject(DomSanitizer);
  private translocoService = inject(TranslocoService);
  private route = inject(ActivatedRoute);

  activeLang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  videoOpen = signal(false);

  youtubeVideos: YouTubeVideo[] = [
    {
      titleKey: 'help.onboarding.resources.videos.items.moocClimat.title',
      videoIdFr: 'lckeZbjkV7A',
      altUrl: 'https://www.youtube.com/watch?v=9PFhrpyWV-w&t=6s',
    },
    {
      titleKey: 'help.onboarding.resources.videos.items.moocCarbon.title',
      videoIdFr: 'lckeZbjkV7A',
      altUrl: 'https://www.youtube.com/watch?v=cB7yYRdFNb8&t=5s',
    },
  ];

  youtubeOpenStates = [signal(false), signal(false)];

  webinars: Webinar[] = [
    {
      titleKey: 'help.onboarding.webinars.items.onboarding.title',
      descriptionKey: 'help.onboarding.webinars.items.onboarding.description',
      duration: '30min',
      registrationUrls: WEBINAR_REGISTRATION_URLS.onboarding,
    },
    {
      titleKey: 'help.onboarding.webinars.items.whatsNext.title',
      descriptionKey: 'help.onboarding.webinars.items.whatsNext.description',
      duration: '30min',
      registrationUrls: WEBINAR_REGISTRATION_URLS.whatsNext,
    },
  ];

  /**
   * The documents anyone can hand to their team. Three journey steps point at this section
   * rather than downloading straight from their row: the formats and what each document is
   * for only make sense here — hence the objective / when / how rows, which say in which
   * meeting the document is meant to be opened.
   */
  resources: Resource[] = [
    {
      i18nKey: 'help.onboarding.resources.items.awarenessKit',
      formats: [
        { type: 'PDF', urls: RESOURCE_URLS.awarenessKit.pdf },
        { type: 'PPTX', urls: RESOURCE_URLS.awarenessKit.pptx },
      ],
      icon: 'presentation',
    },
    {
      i18nKey: 'help.onboarding.resources.items.caseStudy',
      formats: [
        { type: 'PDF', urls: RESOURCE_URLS.caseStudy.pdf },
        { type: 'PPTX', urls: RESOURCE_URLS.caseStudy.pptx },
      ],
      icon: 'presentation',
    },
    {
      i18nKey: 'help.onboarding.resources.items.whatsNext',
      formats: [
        { type: 'PDF', urls: RESOURCE_URLS.whatsNext.pdf },
        { type: 'PPTX', urls: RESOURCE_URLS.whatsNext.pptx },
      ],
      icon: 'presentation',
    },
  ];

  readonly resourceDetails = RESOURCE_DETAILS;

  ngOnInit(): void {
    const block = this.route.snapshot.queryParamMap.get(
      'block',
    ) as HelpResourceBlock | null;

    if (block) {
      this.openBlock(block);
    }
  }

  get videoUrl(): string {
    const lang = this.activeLang();
    return (
      ONBOARDING_VIDEO_URLS[lang as keyof typeof ONBOARDING_VIDEO_URLS] ??
      ONBOARDING_VIDEO_URLS.en
    );
  }

  resourceUrl(urls: Record<string, string>): string {
    return urls[this.activeLang()] ?? urls['en'];
  }

  embedUrl(videoId: string): SafeResourceUrl {
    const lang = this.activeLang();
    const params =
      lang === 'fr' ? '' : '?cc_load_policy=1&cc_lang_pref=en&hl=en';
    return this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube.com/embed/${videoId}${params}`,
    );
  }

  /** Unfolds the block a journey step points at and brings it into view. */
  private openBlock(block: HelpResourceBlock): void {
    if (block === 'introVideo') {
      this.videoOpen.set(true);
    }
    if (block === 'moocs') {
      this.youtubeOpenStates.forEach((state) => state.set(true));
    }

    // Deferred so the target is in the DOM, and past the router's own scroll reset.
    setTimeout(() =>
      document
        .getElementById(`help-block-${block}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  }
}
